if (!customElements.get('shooting-results')) {
  class ShootingResults extends HTMLElement {
    constructor() {
      super();
      this.rawData = { rbTop: [], cpsaTop: [], others: [] };
    }

    connectedCallback() {
      this.apiUrl = this.dataset.apiUrl;
      this.sectionId = this.dataset.sectionId;

      this.rbTopTableId = 'rb-top-table-' + this.sectionId;
      this.cpsaTopTableId = 'cpsa-top-table-' + this.sectionId;
      this.othersTableId = 'others-table-' + this.sectionId;

      this.searchInput = this.querySelector('#shooting-search');

      this.init();
    }

    init() {
      // RB Top (7 cols), CPSA Top (7 cols), Others (7 cols)
      if (!this.apiUrl || this.apiUrl.includes('your-app-url.com')) {
        console.warn('Shooting Results: Invalid API URL provided.');
        this.showConfigError(this.rbTopTableId, 7);
        this.showConfigError(this.cpsaTopTableId, 7);
        this.showConfigError(this.othersTableId, 7);
        return;
      }

      // Show loading state
      this.showLoadingState(this.rbTopTableId, 7);
      this.showLoadingState(this.cpsaTopTableId, 7);
      this.showLoadingState(this.othersTableId, 7);

      this.fetchAndRenderResults().then((data) => {
        if (data) this.rawData = data;
      });

      if (this.searchInput) {
        this.searchInput.addEventListener('input', (e) => this.handleSearch(e));
      }
    }

    handleSearch(e) {
      const query = e.target.value.toLowerCase();

      const filteredRB = this.rawData.rbTop.filter((entry) =>
        entry.name.toLowerCase().includes(query)
      );
      const filteredCPSA = this.rawData.cpsaTop.filter((entry) =>
        entry.name.toLowerCase().includes(query)
      );
      const filteredOthers = this.rawData.others.filter((entry) =>
        entry.name.toLowerCase().includes(query)
      );

      this.renderTable(this.rbTopTableId, filteredRB, { showRank: true, hideCPSA: true });
      this.renderTable(this.cpsaTopTableId, filteredCPSA, { showRank: true, hideRB: true });
      this.renderTable(this.othersTableId, filteredOthers, { showRank: false });
    }

    showLoadingState(tableId, colSpan) {
      const table = document.getElementById(tableId);
      if (!table) return;
      const tbody = table.querySelector('tbody');
      if (!tbody) return;
      tbody.innerHTML = '';
      const row = tbody.insertRow();
      row.className = 'loading-row';
      const cell = row.insertCell();
      cell.colSpan = colSpan;
      cell.innerHTML = '<span class="shooting-spinner"></span> Loading results...';
    }

    showErrorState(tableId, colSpan) {
      const table = document.getElementById(tableId);
      if (!table) return;
      const tbody = table.querySelector('tbody');
      if (!tbody) return;
      tbody.innerHTML = '';
      const row = tbody.insertRow();
      row.className = 'error-row';
      const cell = row.insertCell();
      cell.colSpan = colSpan;
      cell.textContent = 'Error loading results. Please reload page.';
    }

    showConfigError(tableId, colSpan) {
      const table = document.getElementById(tableId);
      if (!table) return;
      const tbody = table.querySelector('tbody');
      if (!tbody) return;
      tbody.innerHTML = '';
      const row = tbody.insertRow();
      row.className = 'error-row';
      const cell = row.insertCell();
      cell.colSpan = colSpan;
      cell.textContent = 'Please configure a valid API URL in the section settings.';
    }

    async fetchAndRenderResults() {
      try {
        const response = await fetch(this.apiUrl);
        if (!response.ok) throw new Error('Failed to fetch results');

        const data = await response.json();

        const rbTop = (data.top50 || [])
          .filter((entry) => entry.type === 'PRB' || entry.type === 'RB')
          .slice(0, 20);
        const cpsaTop = (data.top50 || []).filter((entry) => entry.type === 'CPSA').slice(0, 30);
        const others = data.others || [];

        this.renderTable(this.rbTopTableId, rbTop, { showRank: true, hideCPSA: true });
        this.renderTable(this.cpsaTopTableId, cpsaTop, { showRank: true, hideRB: true });
        this.renderTable(this.othersTableId, others, { showRank: false });

        return { rbTop, cpsaTop, others };
      } catch (error) {
        console.error('Error fetching shooting results:', error);
        this.showErrorState(this.rbTopTableId, 7);
        this.showErrorState(this.cpsaTopTableId, 7);
        this.showErrorState(this.othersTableId, 7);
      }
    }

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}/${mm}/${dd}`;
    }

    renderEmptyState(tbody, options) {
      const row = tbody.insertRow();
      const cell = row.insertCell();
      let colSpan = options.showRank ? 8 : 7;
      if (options.hideCPSA) colSpan--;
      if (options.hideRB) colSpan--;

      cell.colSpan = colSpan;
      cell.textContent = 'No entries found.';
      cell.style.textAlign = 'center';
      cell.style.padding = '32px';
      cell.style.color = '#999';
    }

    renderRow(tbody, entry, index, options) {
      const row = tbody.insertRow();

      if (options.showRank) {
        const rankCell = row.insertCell();
        rankCell.textContent = index + 1;
        rankCell.style.fontWeight = '600';
      }

      const nameCell = row.insertCell();
      nameCell.textContent = entry.name;
      nameCell.style.fontWeight = '500';

      const dateCell = row.insertCell();
      dateCell.textContent = this.formatDate(entry.date);

      const timeCell = row.insertCell();
      timeCell.textContent = entry.time || '-';

      if (!options.hideCPSA) {
        const cpsaHandicapCell = row.insertCell();
        cpsaHandicapCell.textContent = entry.type === 'CPSA' ? entry.handicap : '-';
      }

      if (!options.hideRB) {
        const rbHandicapCell = row.insertCell();
        rbHandicapCell.textContent =
          entry.type === 'PRB' || entry.type === 'RB' ? entry.handicap : '-';
      }

      const scoreCell = row.insertCell();
      scoreCell.textContent = entry.score;

      const totalCell = row.insertCell();
      totalCell.textContent = entry.total;
      totalCell.style.fontWeight = '600';
      totalCell.style.color = '#000';
    }

    renderTable(tableId, entries, options = {}) {
      const table = document.getElementById(tableId);
      if (!table) return;

      const tbody = table.querySelector('tbody');
      if (!tbody) return;

      tbody.innerHTML = '';

      const tableOptions = {
        showRank: options.showRank || false,
        hideCPSA: options.hideCPSA || false,
        hideRB: options.hideRB || false,
      };

      if (!entries || entries.length === 0) {
        this.renderEmptyState(tbody, tableOptions);
        return;
      }

      entries.forEach((entry, index) => this.renderRow(tbody, entry, index, tableOptions));
    }
  }

  customElements.define('shooting-results', ShootingResults);
}
